import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";

import { useSuccessNotification } from "../../hooks/use-success-notification";

export default function SuccessNotification({
  autoHide = true,
  duration = 2000,
}: {
  autoHide?: boolean;
  duration?: number;
}) {
  const { state, hide } = useSuccessNotification();
  const { show, title, message } = state;

  const [isVisible, setIsVisible] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [showCircle, setShowCircle] = useState(false);
  const [showCheck, setShowCheck] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setShowCircle(false);
      setShowCheck(false);

      setTimeout(() => setAnimate(true), 50);
      setTimeout(() => setShowCircle(true), 300);
      setTimeout(() => setShowCheck(true), 300);

      if (autoHide) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        return () => clearTimeout(timer);
      }
    } else {
      handleClose();
    }
  }, [show, autoHide, duration]);

  useEffect(() => {
    if (animate) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [animate]);

  const handleClose = () => {
    setAnimate(false);
    setShowCircle(false);
    setShowCheck(false);
    setTimeout(() => {
      setIsVisible(false);
      hide();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${
        animate ? "bg-[#00000075] bg-opacity-30" : "bg-[#00000075] bg-opacity-0"
      }`}
    >
      <div
        className={`
          relative bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-sm w-full
          transform transition-all duration-300 ease-out
          ${animate ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-4"}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Đóng */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>

        {/* Icon thành công */}
        <div className="flex justify-center mb-6">
          <div className="relative w-16 h-16">
            <svg className="absolute inset-0 w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
              <circle
                cx="32"
                cy="32"
                r="28"
                fill="none"
                stroke="#22c55e"
                strokeWidth="3"
                strokeLinecap="round"
                className={`transition-all duration-700 ease-out ${
                  showCircle ? "stroke-dasharray-[176] stroke-dashoffset-[0]" : "stroke-dasharray-[176] stroke-dashoffset-[176]"
                }`}
                style={{
                  strokeDasharray: "176",
                  strokeDashoffset: showCircle ? "0" : "176",
                  transitionProperty: "stroke-dashoffset",
                  transitionDuration: "700ms",
                  transitionTimingFunction: "ease-out",
                }}
              />
            </svg>

            <div className="absolute inset-0 flex items-center justify-center">
              <Check
                className={`w-8 h-8 text-green-500 transform transition-all duration-300 ease-out ${
                  showCheck ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-50 translate-y-2"
                }`}
                strokeWidth={3}
              />
            </div>
          </div>
        </div>

        {/* Nội dung */}
        <div className="text-center">
          <h3
            className={`text-2xl font-bold text-gray-800 mb-3 transition-all duration-300 delay-200 ${
              animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            {title}
          </h3>
          <p
            className={`text-gray-600 leading-relaxed transition-all duration-300 delay-250 ${
              animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}
